import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Award, Rocket, Wallet, ArrowRight, ArrowDown, Lightbulb, Shield, Zap, Activity, HardHat, Star, Sparkles, ArrowUp } from 'lucide-react';
import Header from "@/components/Header";
import CoreValues from "@/components/CoreValues";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import sportsPerformanceBg from "@/assets/sports-performance-bg.jpg";
import militaryDefenseBg from "@/assets/military-defense-bg.jpg";
import industrialSafetyBg from "@/assets/industrial-safety-bg.jpg";
import thermalRegulationBg from "@/assets/thermal-regulation-bg.jpg";
import expertiseBg from "@/assets/expertise-bg.jpg";
import resultsBg from "@/assets/results-bg.jpg";
import flexibilityBg from "@/assets/flexibility-bg.jpg";
import supportBg from "@/assets/support-bg.jpg";

const Index = () => {
  const [featuredVisible, setFeaturedVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const [applicationsVisible, setApplicationsVisible] = useState(false);
  const [discoverVisible, setDiscoverVisible] = useState(false);
  
  const featuredRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);
  const applicationsRef = useRef<HTMLElement>(null);
  const discoverRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === featuredRef.current) {
              setFeaturedVisible(true);
            } else if (entry.target === processRef.current) {
              setProcessVisible(true);
            } else if (entry.target === applicationsRef.current) {
              setApplicationsVisible(true);
            } else if (entry.target === discoverRef.current) {
              setDiscoverVisible(true);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (featuredRef.current) observer.observe(featuredRef.current);
    if (processRef.current) observer.observe(processRef.current);
    if (applicationsRef.current) observer.observe(applicationsRef.current);
    if (discoverRef.current) observer.observe(discoverRef.current);
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const processColors = [
    { bg: "bg-blue-50", accent: "text-blue-600", border: "border-blue-200", numColor: "text-blue-300" },
    { bg: "bg-sky-50", accent: "text-sky-600", border: "border-sky-200", numColor: "text-sky-300" },
    { bg: "bg-indigo-50", accent: "text-indigo-600", border: "border-indigo-200", numColor: "text-indigo-300" },
    { bg: "bg-cyan-50", accent: "text-cyan-600", border: "border-cyan-200", numColor: "text-cyan-300" }
  ];

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <Hero />
      
      {/* Client Benefits Section */}
      <section id="applications" className="py-20" ref={applicationsRef}>
        <div className="max-w-none mx-4 lg:mx-8">
          <div className="text-center mb-12">
            <span className={cn(
              "inline-block px-3 py-1 bg-primary/10 rounded-lg text-sm font-medium mb-4 transition-all duration-700",
              applicationsVisible ? "opacity-100" : "opacity-0"
            )}>CLIENT BENEFITS</span>
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-6 transition-all duration-700",
              applicationsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Why Choose Keplerion Tech
            </h2>
            <p className={cn(
              "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              applicationsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Helping your business achieve measurable success and stay one step ahead in a competitive world.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Award,
                title: "Uncompromising Quality",
                description: "Senior engineers on every project, with code reviews and thorough testing before anything ships. We build software that is reliable, maintainable, and built to the highest standard.",
                gradient: "from-yellow-50 to-orange-50",
                iconBg: "bg-primary/10",
                iconColor: "text-primary",
                bgImage: expertiseBg
              },
              {
                icon: Rocket,
                title: "Rapid Delivery",
                description: "Our streamlined process and custom-built tools get you from idea to working software faster than traditional development teams. You see progress early and often, so nothing sits in limbo.",
                gradient: "from-green-50 to-emerald-50", 
                iconBg: "bg-green-100",
                iconColor: "text-green-600",
                bgImage: resultsBg
              },
              {
                icon: Wallet,
                title: "Competitive Rates",
                description: "Our nearshore team and lean structure keep costs well below typical US agency rates, without cutting corners. Transparent, fixed-price quotes mean no surprise invoices.",
                gradient: "from-purple-50 to-indigo-50",
                iconBg: "bg-violet-100", 
                iconColor: "text-violet-400",
                bgImage: flexibilityBg
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={cn(
                  "relative overflow-hidden rounded-xl p-8 transition-all duration-700 hover:shadow-lg group cursor-pointer border border-muted",
                  applicationsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
                )}
                style={{ 
                  transitionDelay: `${index * 150}ms`,
                  backgroundImage: `url(${item.bgImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Background overlay */}
                <div className="absolute inset-0 bg-background/85 group-hover:bg-background/75 transition-all duration-300" />
                
                <div className="relative z-10">
                  <div className={`w-12 h-12 ${item.iconBg} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-foreground group-hover:text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground group-hover:text-foreground/90 text-base leading-relaxed">{item.description}</p>
                </div>
                
                {/* Animated bottom line */}
                <div className="absolute bottom-4 left-6 right-6 h-0.5">
                  <div className={`h-full scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left ${
                    item.title === 'Results' ? 'bg-green-500' : 
                    item.title === 'Flexibility' ? 'bg-violet-400' : 
                    'bg-primary'
                  }`} />
                </div>
                
                {/* Subtle background pattern */}
                <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_30%_20%,_rgba(120,120,120,0.3)_0%,_transparent_50%)]" />
                <div className="absolute top-0 right-0 w-24 h-24 -mt-12 -mr-12 rounded-full bg-white/5" />
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Core Values Section - using id="values" to match the ID in CoreValues component */}
      <section id="values">
        <CoreValues />
      </section>
      
      {/* Discover Section (hidden; wrapped in `false &&` because it contains inner JSX comments that can't be nested in a block comment) */}
      {false && (
      <section className="py-20 px-6 md:px-10 bg-muted/20" ref={discoverRef}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className={cn(
              "inline-block px-3 py-1 bg-primary/10 rounded-lg text-sm font-medium mb-4 transition-all duration-700",
              discoverVisible ? "opacity-100" : "opacity-0"
            )}>
              DISCOVER
            </span>
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-6 transition-all duration-700",
              discoverVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Explore Our Capabilities
            </h2>
            <p className={cn(
              "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              discoverVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Discover how our services can transform your business across different domains and industries.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              {
                title: "Ideas in Action",
                description: "Where creative concepts meet practical implementation to deliver software that works.",
                hoverDescription: "We transform innovative ideas into tangible results through strategic planning, agile execution, and collaborative development. Every concept is carefully crafted into actionable solutions that drive measurable business outcomes.",
                highlight: "Concept to Reality",
                icon: Lightbulb,
                tags: ["Research", "Exploration", "AI/ML", "Future-Tech", "Innovation"]
              },
              {
                title: "Digital Transformation",
                description: "We help you modernize how you work- processes, and experiences to keep pace with what's next.",
                hoverDescription: "We modernize legacy systems and create digital-first strategies that position businesses for the future. Our transformation approach ensures seamless integration and measurable results.",
                highlight: "Business Evolution",
                icon: Shield,
                tags: ["Strategy", "Modernization", "Integration", "Growth"]
              },
              {
                title: "Optimized Operations",
                description: "Behind-the-scenes processes shouldn't slow you down. We fine-tune systems so your team can move faster and smarter.",
                hoverDescription: "We analyze, redesign, and optimize your business operations to eliminate bottlenecks, reduce waste, and enhance performance. Our approach focuses on sustainable efficiency gains that scale with your growth.",
                highlight: "Efficiency & Performance",
                icon: Zap,
                tags: ["Streamlining", "Efficiency", "Performance", "Optimization"]
              }
            ].map((item, index) => {
              const DiscoverCard = () => {
                const [isHovered, setIsHovered] = useState(false);
                
                return (
                  <div 
                    className={cn(
                      "h-[65vh] w-full max-w-[450px] mx-auto transition-all duration-700",
                      discoverVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
                    )}
                    style={{ transitionDelay: `${index * 200}ms` }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <div className={cn(
                      "h-full overflow-hidden relative group cursor-pointer perspective-1000 rounded-xl",
                      "transform-gpu hover:shadow-xl transition-all duration-500",
                      index === 0 ? "bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200" :
                      index === 1 ? "bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200" :
                      "bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200"
                    )}>
                      {!isHovered ? (
                        // Initial state - light background
                        <>
                          {/* Floating particles effect */}
                          <div className="absolute inset-0 opacity-30 pointer-events-none overflow-hidden">
                            {[...Array(10)].map((_, i) => (
                              <div 
                                key={i}
                                className={cn(
                                  "absolute rounded-full",
                                  index === 0 ? "bg-blue-600/40" :
                                  index === 1 ? "bg-indigo-600/40" :
                                  "bg-cyan-600/40"
                                )} 
                                style={{
                                  width: `${Math.random() * 4 + 1}px`,
                                  height: `${Math.random() * 4 + 1}px`,
                                  top: `${Math.random() * 100}%`,
                                  left: `${Math.random() * 100}%`,
                                  animation: `float ${Math.random() * 10 + 15}s linear infinite`,
                                  opacity: Math.random() * 0.5 + 0.3,
                                }}
                              />
                            ))}
                          </div>

                          {/* Icon in the corner */}
                          <div className={cn(
                            "absolute top-4 right-4 z-10",
                            index === 0 ? "text-blue-700" :
                            index === 1 ? "text-indigo-700" :
                            "text-cyan-700"
                          )}>
                            <div className={cn(
                              "absolute inset-0 rounded-full filter blur-xl w-10 h-10 -m-2 animate-pulse",
                              index === 0 ? "bg-blue-500/20" :
                              index === 1 ? "bg-indigo-500/20" :
                              "bg-cyan-500/20"
                            )}></div>
                            <div className={cn(
                              "text-3xl relative p-2 rounded-full backdrop-blur-sm shadow-lg transform hover:rotate-12 hover:scale-110 transition-all duration-300",
                              index === 0 ? "bg-blue-100/80 border border-blue-300" :
                              index === 1 ? "bg-indigo-100/80 border border-indigo-300" :
                              "bg-cyan-100/80 border border-cyan-300"
                            )}>
                              <div className="animate-subtle-pulse relative">
                                <item.icon className="w-6 h-6" />
                                <div className="absolute inset-0 blur-sm opacity-70"></div>
                              </div>
                            </div>
                          </div>

                          {/* Background effects */}
                          <div className="absolute inset-0 opacity-20 pointer-events-none">
                            <div className={cn(
                              "absolute right-0 top-0 w-60 h-60 rounded-full blur-2xl",
                              index === 0 ? "bg-blue-200/50" :
                              index === 1 ? "bg-indigo-200/50" :
                              "bg-cyan-200/50"
                            )}></div>
                            <div className={cn(
                              "absolute left-0 bottom-0 w-60 h-60 rounded-full blur-2xl",
                              index === 0 ? "bg-blue-300/30" :
                              index === 1 ? "bg-indigo-300/30" :
                              "bg-cyan-300/30"
                            )}></div>
                            <div className={cn(
                              "absolute left-1/4 top-1/3 w-16 h-16 rounded-full blur-xl animate-pulse",
                              index === 0 ? "bg-blue-100/40" :
                              index === 1 ? "bg-indigo-100/40" :
                              "bg-cyan-100/40"
                            )}></div>
                            <div className={cn(
                              "absolute right-1/4 bottom-1/3 w-24 h-24 rounded-full blur-xl animate-pulse",
                              index === 0 ? "bg-blue-200/30" :
                              index === 1 ? "bg-indigo-200/30" :
                              "bg-cyan-200/30"
                            )} style={{animationDelay: "1.5s"}}></div>
                          </div>

                          {/* Line-based background styling inspired by team values */}
                          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                            <div className={cn(
                              "absolute top-1/4 left-0 w-full h-px transform rotate-[-35deg] origin-left",
                              index === 0 ? "bg-blue-400/20" :
                              index === 1 ? "bg-indigo-400/20" :
                              "bg-cyan-400/20"
                            )}></div>
                            <div className={cn(
                              "absolute top-3/4 right-0 w-full h-px transform rotate-[-35deg] origin-right",
                              index === 0 ? "bg-blue-400/15" :
                              index === 1 ? "bg-indigo-400/15" :
                              "bg-cyan-400/15"
                            )}></div>
                            <div className={cn(
                              "absolute top-2/3 left-1/2 w-32 h-32 rounded-full border transform -translate-x-1/2 -translate-y-1/2 opacity-20",
                              index === 0 ? "border-blue-300/20" :
                              index === 1 ? "border-indigo-300/20" :
                              "border-cyan-300/20"
                            )}></div>
                            <div className={cn(
                              "absolute top-1/4 right-1/4 w-20 h-20 rounded-full border opacity-20",
                              index === 0 ? "border-blue-300/10" :
                              index === 1 ? "border-indigo-300/10" :
                              "border-cyan-300/10"
                            )}></div>
                          </div>

                          {/* Artistic corner accents */}
                          <div className="absolute top-0 left-0 w-16 h-16 overflow-hidden opacity-30">
                            <div className={cn(
                              "absolute w-32 h-1 rotate-45 translate-x-[-50%] translate-y-[500%]",
                              index === 0 ? "bg-blue-400/30" :
                              index === 1 ? "bg-indigo-400/30" :
                              "bg-cyan-400/30"
                            )}></div>
                          </div>
                          <div className="absolute bottom-0 right-0 w-16 h-16 overflow-hidden opacity-30">
                            <div className={cn(
                              "absolute w-32 h-1 rotate-45 translate-x-[-50%] translate-y-[500%]",
                              index === 0 ? "bg-blue-400/30" :
                              index === 1 ? "bg-indigo-400/30" :
                              "bg-cyan-400/30"
                            )}></div>
                          </div>

                          {/* Content area */}
                          <div className="flex flex-col h-full relative z-10">
                            <div className="flex-1 flex flex-col justify-end pt-8 pb-4 px-6 mt-auto">
                              <div className="space-y-3">
                                <div className="relative overflow-hidden">
                                  <h3 className={cn(
                                    "text-4xl font-bold tracking-tight leading-tight",
                                    index === 0 ? "text-blue-800" :
                                    index === 1 ? "text-indigo-800" :
                                    "text-cyan-800"
                                  )}>
                                    {item.title}
                                  </h3>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <div className={cn(
                                    "w-10 h-1 rounded-full",
                                    index === 0 ? "bg-blue-600/50" :
                                    index === 1 ? "bg-indigo-600/50" :
                                    "bg-cyan-600/50"
                                  )}></div>
                                  <div className={cn(
                                    "w-2 h-2 rounded-full",
                                    index === 0 ? "bg-blue-700/60" :
                                    index === 1 ? "bg-indigo-700/60" :
                                    "bg-cyan-700/60"
                                  )}></div>
                                  <div className={cn(
                                    "w-1 h-1 rounded-full",
                                    index === 0 ? "bg-blue-500/40" :
                                    index === 1 ? "bg-indigo-500/40" :
                                    "bg-cyan-500/40"
                                  )}></div>
                                </div>
                                
                                <p className={cn(
                                  "text-lg leading-relaxed font-light",
                                  index === 0 ? "text-blue-800" :
                                  index === 1 ? "text-indigo-800" :
                                  "text-cyan-800"
                                )}>
                                  {item.description}
                                </p>
                              </div>
                            </div>

                            {/* Tags section */}
                            <div className="flex flex-wrap gap-1 px-6 pb-6">
                              {item.tags.map((tag, i) => (
                                <div 
                                  key={i} 
                                  className={cn(
                                    "px-2 py-1 rounded-full text-sm font-medium backdrop-blur-sm border shadow-lg hover:scale-105 transition-all duration-300",
                                    index === 0 ? "bg-blue-100/60 text-blue-700 border-blue-200/50 hover:bg-blue-200/70" :
                                    index === 1 ? "bg-indigo-100/60 text-indigo-700 border-indigo-200/50 hover:bg-indigo-200/70" :
                                    "bg-cyan-100/60 text-cyan-700 border-cyan-200/50 hover:bg-cyan-200/70"
                                  )}
                                >
                                  {tag}
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        // Hover state - dark background
                        <>
                          {/* Enhanced icon with arrow */}
                          <div className="absolute top-4 right-4 z-20">
                            <div className={cn(
                              "absolute inset-0 rounded-full filter blur-xl w-12 h-12 -m-3",
                              index === 0 ? "bg-blue-300/30" :
                              index === 1 ? "bg-indigo-300/30" :
                              "bg-cyan-300/30"
                            )}></div>
                            <div className={cn(
                              "text-3xl p-2 rounded-full backdrop-blur-md shadow-xl transform hover:rotate-12 transition-all duration-500 relative",
                              index === 0 ? "filter drop-shadow-[0_0_15px_rgba(219,234,254,0.8)] bg-blue-100/20 border border-blue-300/30" :
                              index === 1 ? "filter drop-shadow-[0_0_15px_rgba(224,231,255,0.8)] bg-indigo-100/20 border border-indigo-300/30" :
                              "filter drop-shadow-[0_0_15px_rgba(207,250,254,0.8)] bg-cyan-100/20 border border-cyan-300/30"
                            )}>
                              <div className={cn(
                                "relative",
                                index === 0 ? "text-blue-100" :
                                index === 1 ? "text-indigo-100" :
                                "text-cyan-100"
                              )}>
                                <item.icon className="w-6 h-6" />
                                <div className="absolute -top-2 -right-2 transform rotate-12">
                                  <Star className="h-4 w-4 text-white filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                  
                                  {/* Starburst effect behind the star */}
                                  <div className="absolute inset-0 -z-10">
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-white/80 rounded-full blur-sm opacity-60 scale-75 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]"></div>
                                  </div>
                                  
                                  {/* Tiny sparkle accents */}
                                  <Sparkles className="absolute -top-1 -right-1 h-1.5 w-1.5 text-white animate-[pulse_3s_ease-in-out_infinite]" style={{animationDelay: "0.5s"}} />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Frosted glass overlay with different color palettes */}
                          <div className={cn(
                            "absolute inset-0 backdrop-blur-md",
                            index === 0 ? "bg-gradient-to-br from-blue-900/90 to-blue-800/95" :
                            index === 1 ? "bg-gradient-to-br from-indigo-900/90 to-indigo-800/95" :
                            "bg-gradient-to-br from-cyan-900/90 to-cyan-800/95"
                          )}></div>
                          
                          {/* Animated background patterns with matching colors */}
                          <div className="absolute inset-0 overflow-hidden">
                            <div className={cn(
                              "absolute -right-10 -top-10 w-60 h-60 rounded-full blur-2xl animate-pulse opacity-30",
                              index === 0 ? "bg-gradient-to-br from-blue-100/10 to-transparent" :
                              index === 1 ? "bg-gradient-to-br from-indigo-100/10 to-transparent" :
                              "bg-gradient-to-br from-cyan-100/10 to-transparent"
                            )}></div>
                            <div className={cn(
                              "absolute -left-10 -bottom-10 w-60 h-60 rounded-full blur-2xl animate-pulse opacity-30",
                              index === 0 ? "bg-gradient-to-br from-blue-200/10 to-transparent" :
                              index === 1 ? "bg-gradient-to-br from-indigo-200/10 to-transparent" :
                              "bg-gradient-to-br from-cyan-200/10 to-transparent"
                            )} style={{animationDelay: "1s"}}></div>
                            <div className={cn(
                              "absolute inset-0",
                              index === 0 ? "bg-[radial-gradient(circle_at_50%_50%,rgba(219,234,254,0.1),transparent_50%)]" :
                              index === 1 ? "bg-[radial-gradient(circle_at_50%_50%,rgba(224,231,255,0.1),transparent_50%)]" :
                              "bg-[radial-gradient(circle_at_50%_50%,rgba(207,250,254,0.1),transparent_50%)]"
                            )}></div>
                          </div>
                          
                          {/* Moving particles */}
                          <div className="absolute inset-0 overflow-hidden opacity-20">
                            {[...Array(15)].map((_, i) => (
                              <div 
                                key={i}
                                className="absolute rounded-full bg-blue-100" 
                                style={{
                                  width: `${Math.random() * 2 + 1}px`,
                                  height: `${Math.random() * 2 + 1}px`,
                                  top: `${Math.random() * 100}%`,
                                  left: `${Math.random() * 100}%`,
                                  animation: `float ${Math.random() * 10 + 15}s linear infinite`,
                                }}
                              />
                            ))}
                          </div>
                          
                          {/* Main content */}
                          <div className="flex flex-col h-full relative z-10">
                            <div className="pt-16 pb-1">
                              <div className="space-y-1 px-6">
                                <h3 className={cn(
                                  "text-3xl font-bold mb-1 relative",
                                  index === 0 ? "text-blue-100" :
                                  index === 1 ? "text-indigo-100" :
                                  "text-cyan-100"
                                )}>
                                  {item.title}
                                  <span className={cn(
                                    "absolute -bottom-1 left-0 w-1/3 h-1 rounded-full",
                                    index === 0 ? "bg-blue-300/50" :
                                    index === 1 ? "bg-indigo-300/50" :
                                    "bg-cyan-300/50"
                                  )}></span>
                                </h3>
                              </div>
                            </div>
                            
                            <div className="flex-1 pt-2 px-6">
                              <div className="prose prose-invert max-w-none">
                                <p className={cn(
                                  "text-base leading-relaxed font-light",
                                  index === 0 ? "text-blue-100" :
                                  index === 1 ? "text-indigo-100" :
                                  "text-cyan-100"
                                )}>
                                  {item.hoverDescription}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-1 my-3">
                                <div className={cn(
                                  "flex-grow h-px bg-gradient-to-r from-transparent to-transparent",
                                  index === 0 ? "via-blue-300/30" :
                                  index === 1 ? "via-indigo-300/30" :
                                  "via-cyan-300/30"
                                )}></div>
                                <Star className="h-2 w-2 text-white/80" />
                                <div className={cn(
                                  "flex-grow h-px bg-gradient-to-r from-transparent to-transparent",
                                  index === 0 ? "via-blue-300/30" :
                                  index === 1 ? "via-indigo-300/30" :
                                  "via-cyan-300/30"
                                )}></div>
                              </div>
                              
                              <div className={cn(
                                "relative pl-3 border-l-2 italic text-sm",
                                index === 0 ? "border-blue-300/40 text-blue-200" :
                                index === 1 ? "border-indigo-300/40 text-indigo-200" :
                                "border-cyan-300/40 text-cyan-200"
                              )}>
                                <p>"{item.highlight} represents the future of business innovation and technological advancement."</p>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              };
              
              return <DiscoverCard key={index} />;
            })}
          </div>
        </div>
      </section>
      )}
      
      {/* Our Services Section (hidden) */}
      {/* <Services /> */}
      
      {/* Featured Work Section - with enhanced animations */}
      {/*<section className="py-20 px-6 md:px-10 bg-secondary/5" ref={featuredRef}>*/}
      {/*  <div className="max-w-7xl mx-auto">*/}
      {/*    <div className="text-center mb-12">*/}
      {/*      <span className={cn(*/}
      {/*        "inline-block px-3 py-1 bg-primary/10 rounded-lg text-sm font-medium mb-4 transition-all duration-700",*/}
      {/*        featuredVisible ? "opacity-100" : "opacity-0"*/}
      {/*      )}>FEATURED WORK</span>*/}
      {/*      <h2 className={cn(*/}
      {/*        "text-3xl md:text-4xl font-bold mb-6 transition-all duration-700",*/}
      {/*        featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"*/}
      {/*      )}>Our Latest Projects</h2>*/}
      {/*      <p className={cn(*/}
      {/*        "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",*/}
      {/*        featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"*/}
      {/*      )}>*/}
      {/*        Take a look at some of our recent work that showcases our expertise and capabilities in creating innovative software solutions.*/}
      {/*      </p>*/}
      {/*    </div>*/}
      {/*    */}
      {/*    <div className="grid md:grid-cols-2 gap-8">*/}
      {/*      <div className={cn(*/}
      {/*        "group relative overflow-hidden rounded-xl transition-all duration-700",*/}
      {/*        featuredVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"*/}
      {/*      )}*/}
      {/*        style={{ transitionDelay: "300ms" }}*/}
      {/*      >*/}
      {/*        <img */}
      {/*          src="/placeholder.svg" */}
      {/*          alt="AI Customer Service Platform"*/}
      {/*          className="w-full h-64 object-cover"*/}
      {/*        />*/}
      {/*        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6 text-white">*/}
      {/*          <h3 className="text-xl font-semibold mb-2">AI-Powered Customer Service</h3>*/}
      {/*          <p className="text-sm text-white/80 mb-4">Revolutionizing customer support with intelligent automated responses</p>*/}
      {/*          <Link */}
      {/*            to="/portfolio" */}
      {/*            className="inline-flex items-center text-sm font-medium text-white"*/}
      {/*          >*/}
      {/*            View Project <ArrowRight size={16} className="ml-1" />*/}
      {/*          </Link>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*      */}
      {/*      <div className={cn(*/}
      {/*        "group relative overflow-hidden rounded-xl transition-all duration-700",*/}
      {/*        featuredVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"*/}
      {/*      )}*/}
      {/*        style={{ transitionDelay: "500ms" }}*/}
      {/*      >*/}
      {/*        <img */}
      {/*          src="/placeholder.svg" */}
      {/*          alt="Enterprise Resource Planning System"*/}
      {/*          className="w-full h-64 object-cover"*/}
      {/*        />*/}
      {/*        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6 text-white">*/}
      {/*          <h3 className="text-xl font-semibold mb-2">Enterprise Resource Planning</h3>*/}
      {/*          <p className="text-sm text-white/80 mb-4">Streamlining business operations with our comprehensive ERP solution</p>*/}
      {/*          <Link */}
      {/*            to="/portfolio" */}
      {/*            className="inline-flex items-center text-sm font-medium text-white"*/}
      {/*          >*/}
      {/*            View Project <ArrowRight size={16} className="ml-1" />*/}
      {/*          </Link>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*    */}
      {/*    <div className={cn(*/}
      {/*      "text-center mt-10 transition-all duration-700",*/}
      {/*      featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"*/}
      {/*    )}*/}
      {/*      style={{ transitionDelay: "700ms" }}*/}
      {/*    >*/}
      {/*      <Link */}
      {/*        to="/portfolio" */}
      {/*        className="inline-flex items-center px-6 py-3 rounded-full border border-primary text-primary transition-colors"*/}
      {/*      >*/}
      {/*        View All Projects <ArrowRight size={18} className="ml-2" />*/}
      {/*      </Link>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}
      
      {/* Our Process Section - with animations and improved styling */}
      {/* (hidden)
      <section className="py-20 px-6 md:px-10" ref={processRef}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className={cn(
              "inline-block px-3 py-1 bg-primary/10 rounded-lg text-sm font-medium mb-4 transition-all duration-700",
              processVisible ? "opacity-100" : "opacity-0"
            )}>OUR PROCESS</span>
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-6 transition-all duration-700",
              processVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>How We Work</h2>
            <p className={cn(
              "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
              processVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              Our proven development methodology ensures consistent results and exceptional quality for every project.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Discovery", desc: "We start by understanding your business goals and requirements" },
              { num: "02", title: "Planning", desc: "Developing a strategic roadmap with clear milestones and deliverables" },
              { num: "03", title: "Development", desc: "Building your solution using agile methodologies with regular updates" },
              { num: "04", title: "Deployment", desc: "Seamless implementation with comprehensive testing and support" }
            ].map((step, index) => (
              <div 
                key={index} 
                className={cn(
                  "p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-500 overflow-hidden relative",
                  processColors[index].bg, processColors[index].border, "border",
                  processVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
                )}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={cn("text-6xl font-bold mb-6", processColors[index].numColor)}>{step.num}</div>
                <h3 className={cn("text-xl font-semibold mb-3", processColors[index].accent)}>{step.title}</h3>
                <p className="text-muted-foreground text-base">{step.desc}</p>
                <div className="absolute top-0 right-0 w-16 h-16 -mt-8 -mr-8 rounded-full bg-white opacity-10"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}
      
      {/* Call to Action */}
      <section className="py-20 px-6 md:px-10 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-lg text-muted-foreground mb-10">
            Let's work together to build innovative solutions that drive your business forward. Our team is ready to help you achieve your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/team" 
              className="px-8 py-3 rounded-full bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-all"
            >
              Meet Our Team
            </Link>
            <Link 
              to="/contact" 
              className="px-8 py-3 rounded-full bg-primary text-white font-medium shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all"
            >
              Start a Project
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;
